import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/brand/Icon';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Field';
import { useSubjects, useTeachers } from '@/lib/queries';
import { useI18n } from '@/lib/i18n';

export function FindALesson(): React.JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();

  const { data: subjects } = useSubjects();
  const [subject, setSubject] = useState('');
  const { data: teachers } = useTeachers(subject ? { subject, perPage: 40 } : { perPage: 40 });
  const [teacherId, setTeacherId] = useState('');
  const [day, setDay] = useState('');

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (teacherId) params.set('teacherId', teacherId);
    if (day) params.set('from', day);
    navigate(`/booking/time?${params.toString()}`);
  };

  return (
    <section className="bg-canvas py-14">
      <div className="mv-container">
        <form
          onSubmit={submit}
          className="flex flex-col gap-5 rounded-lg border border-border-subtle bg-surface px-8 py-7 shadow-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-col gap-0.5">
              <h2 className="text-h4 font-semibold text-primary">{t('finder.title')}</h2>
              <p className="text-body-sm text-secondary">{t('finder.sub')}</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/booking/time')}
              className="group inline-flex items-center gap-1.5 text-body-sm font-medium text-accent"
            >
              {t('finder.fullTimetable')}
              <Icon
                name="arrow-right"
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </button>
          </div>

          <div className="flex flex-col items-end gap-4 md:flex-row">
            <Select
              label={t('finder.subject')}
              value={subject}
              onChange={(event) => {
                setSubject(event.target.value);
                setTeacherId('');
              }}
              containerClassName="flex-1"
            >
              <option value="">{t('finder.anySubject')}</option>
              {subjects?.map((item) => (
                <option key={item.slug} value={item.slug}>
                  {item.name}
                </option>
              ))}
            </Select>

            <Select
              label={t('finder.day')}
              value={day}
              onChange={(event) => setDay(event.target.value)}
              containerClassName="flex-1"
            >
              <option value="">{t('finder.anyDay')}</option>
              {nextSevenDays().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              label={t('finder.teacher')}
              value={teacherId}
              onChange={(event) => setTeacherId(event.target.value)}
              containerClassName="flex-1"
            >
              <option value="">{t('finder.anyTeacher')}</option>
              {teachers?.items.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.fullName}
                </option>
              ))}
            </Select>

            <Button type="submit" size="lg" className="w-full md:w-auto">
              {t('finder.submit')}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

function nextSevenDays(): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];
  const today = new Date();

  for (let i = 0; i < 7; i += 1) {
    const date = new Date(today.getTime() + i * 86_400_000);
    const value = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Baku',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
    const label = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Baku',
      weekday: 'long',
      day: 'numeric',
      month: 'short',
    }).format(date);
    out.push({ value, label });
  }

  return out;
}
